import React, { useEffect, useState } from 'react';
import ActionIconButton from './ActionIconButton';
import { useApp } from '../context/AppContext';
import { apiClient } from '../utils/apiClient';
import { getLocationDisplayName } from '../utils/locationUtils';
import './management-forms.css';

const emptyLocation = { name: '', airport: '', airportName: null, city: '', state: '', isDefault: false };
const inputStyle = { padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', width: '100%', height: '40px', minHeight: '40px', boxSizing: 'border-box' };
const airportCodeInputStyle = { ...inputStyle, width: 'calc(4ch + 1rem + 2px)', fontFamily: 'monospace' };
const fieldLabelStyle = { display: 'grid', gridTemplateRows: '1.5em 40px', gap: '0.25rem', minWidth: 0, alignContent: 'start' };
const normalizeAirportCode = value => {
  const code = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? `K${code}` : code;
};
const loadAirport = async (code, sessionId, signal) => {
  const response = await apiClient(`/airport?code=${encodeURIComponent(code)}`, { headers: { 'session-id': sessionId }, signal });
  const result = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(result.error || 'Unable to validate airport');
  if (typeof result.airportCode !== 'string' || !/^[A-Z0-9]{3,5}$/i.test(result.airportCode)
    || String(result.requestedAirportCode || '').toUpperCase() !== code
    || !result.airportData || typeof result.airportData !== 'object' || Array.isArray(result.airportData)
    || !['city', 'state'].every(field => result[field] === null || typeof result[field] === 'string')) {
    throw new Error('The airport lookup returned invalid details.');
  }
  return result;
};

const OrganizationLocations = () => {
  const { locations, fetchLocations, locationError, currentUser } = useApp();
  const [draft, setDraft] = useState(emptyLocation);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [editingLocationId, setEditingLocationId] = useState(null);
  const [inlineDraft, setInlineDraft] = useState(emptyLocation);
  const [airportLookup, setAirportLookup] = useState({ code: '', data: null, error: '', loading: false });
  const airportCode = normalizeAirportCode(draft.airport);
  const airportDetails = airportLookup.code === airportCode ? airportLookup.data : null;
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    setAirportLookup({ code: airportCode, data: null, error: '', loading: false });
    if (!/^[A-Z0-9]{3,5}$/.test(airportCode) || !currentUser?.sessionId) return;
    const timer = setTimeout(async () => {
      setAirportLookup({ code: airportCode, data: null, error: '', loading: true });
      try {
        const data = await loadAirport(airportCode, currentUser.sessionId, controller.signal);
        if (active) setAirportLookup({ code: airportCode, data, error: '', loading: false });
      } catch (error) {
        if (active) setAirportLookup({ code: airportCode, data: null, error: error.message, loading: false });
      }
    }, 350);
    return () => { active = false; clearTimeout(timer); controller.abort(); };
  }, [airportCode, currentUser?.sessionId]);
  const canEdit = ['admin', 'superuser'].includes(currentUser?.user?.orgRole);
  const save = async event => {
    event.preventDefault();
    const payload = {
      name: draft.name.trim(),
      airportCode,
      isDefault: !!draft.isDefault
    };
    if (!payload.name || payload.name.length > 200) {
      setMessage('Location name must contain 1 to 200 characters.');
      return;
    }
    if (!/^[A-Z0-9]{3,5}$/.test(payload.airportCode)) {
      setMessage('Airport code must contain 3 to 5 letters or numbers.');
      return;
    }
    if (draft.id != null) {
      payload.id = Number(draft.id);
      if (!Number.isInteger(payload.id) || payload.id < 1) {
        setMessage('Location ID must be a positive integer.');
        return;
      }
    }
    setBusy(true);
    setMessage('');
    try {
      const details = airportDetails || await loadAirport(airportCode, currentUser.sessionId);
      payload.airportCode = details.airportCode.toUpperCase();
      payload.airportName = details.name;
      payload.city = details.city;
      payload.state = details.state;
      if ((payload.city?.length || 0) > 100 || (payload.state?.length || 0) > 100) throw new Error('Airport city or state exceeds the supported length.');
      setAirportLookup({ code: airportCode, data: details, error: '', loading: false });
      const response = await apiClient('/location', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'session-id': currentUser.sessionId },
        body: JSON.stringify(payload)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to save location');
      await fetchLocations();
      setDraft(emptyLocation);
      setMessage('Location saved.');
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };
  const editLocation = location => {
    setEditingLocationId(location.id);
    setInlineDraft({ id: location.id, name: location.name || '', airport: location.airportCode || location.airport || '', airportName: location.airportName ?? null, isDefault: !!location.isDefault });
    setMessage('');
  };
  const cancelLocationEdit = () => {
    setEditingLocationId(null);
    setInlineDraft(emptyLocation);
  };
  const saveInlineLocation = async location => {
    const name = inlineDraft.name.trim();
    const code = normalizeAirportCode(inlineDraft.airport);
    if (!name || name.length > 200) {
      setMessage('Location name must contain 1 to 200 characters.');
      return;
    }
    if (!/^[A-Z0-9]{3,5}$/.test(code)) {
      setMessage('Airport code must contain 3 to 5 letters or numbers.');
      return;
    }
    setBusy(true);
    setMessage('');
    try {
      const details = await loadAirport(code, currentUser.sessionId);
      if ((details.city?.length || 0) > 100 || (details.state?.length || 0) > 100) throw new Error('Airport city or state exceeds the supported length.');
      const response = await apiClient('/location', {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'session-id': currentUser.sessionId },
        body: JSON.stringify({ id: Number(location.id), name, airportCode: details.airportCode.toUpperCase(), airportName: inlineDraft.airportName == null ? details.name : inlineDraft.airportName.trim(), city: details.city, state: details.state, isDefault: !!inlineDraft.isDefault })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to save location');
      await fetchLocations();
      cancelLocationEdit();
      setMessage('Location updated.');
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };
  const remove = async location => {
    if (!window.confirm(`Delete ${getLocationDisplayName(location)}?`)) return;
    setBusy(true);
    try {
      const response = await apiClient(`/location/${location.id}`, { method: 'DELETE', headers: { 'session-id': currentUser.sessionId } });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Unable to delete location');
      if (draft.id === location.id) setDraft(emptyLocation);
      if (editingLocationId === location.id) cancelLocationEdit();
      await fetchLocations();
      setMessage('Location deleted.');
    } catch (error) { setMessage(error.message); }
    finally { setBusy(false); }
  };
  return (
    <section className="management-form">
      <h3>Organization Locations</h3>
      {(message || locationError) && <p role="status">{message || locationError}</p>}
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '1rem' }}>
        <thead><tr>{['Location', 'Airport', 'City', 'State', 'Primary', ''].map(label => <th key={label} style={{ textAlign: 'left', padding: '0.5rem', borderBottom: '1px solid #ccc' }}>{label}</th>)}</tr></thead>
        <tbody>{locations.map(location => {
          const displayName = getLocationDisplayName(location);
          return (
          <tr key={location.id}>
            <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee', verticalAlign: 'bottom' }}>{editingLocationId === location.id
              ? <label style={fieldLabelStyle}>Location name<input aria-label={`Location name for ${displayName}`} disabled={busy} maxLength={200} value={inlineDraft.name} onChange={event => setInlineDraft(previous => ({ ...previous, name: event.target.value }))} style={inputStyle} /></label>
              : displayName}</td>
            <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee', verticalAlign: 'bottom' }}>
              {editingLocationId === location.id
                ? <div style={{ display: 'grid', width: 'fit-content', gridTemplateColumns: 'max-content minmax(0, 200px)', maxWidth: '100%', gap: '0.5rem', alignItems: 'end' }}>
                  <label style={fieldLabelStyle}>Airport code<input aria-label={`Airport code for ${displayName}`} disabled={busy} size={4} maxLength={5} value={inlineDraft.airport} onChange={event => {
                    const airport = event.target.value;
                    setInlineDraft(previous => ({ ...previous, airport, airportName: normalizeAirportCode(airport) === normalizeAirportCode(previous.airport) ? previous.airportName : null }));
                  }} style={airportCodeInputStyle} /></label>
                  <label style={fieldLabelStyle}>Airport name<input aria-label={`Airport name for ${displayName}`} disabled={busy} value={inlineDraft.airportName ?? ''} placeholder="Airport lookup name" onChange={event => setInlineDraft(previous => ({ ...previous, airportName: event.target.value }))} style={{ ...inputStyle, maxWidth: '200px', minWidth: 0 }} /></label>
                </div>
                : <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <span>{location.airportName ? `${location.airportName} (${location.airportCode || location.airport || 'No code'})` : location.airportCode || location.airport || 'Not set'}</span>
              </div>}
            </td>
            {[location.city, location.state].map((value, index) => <td key={index} style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>{value}</td>)}
            <td style={{ padding: '0.5rem', borderBottom: '1px solid #eee' }}>{editingLocationId === location.id
              ? <input aria-label={`Primary location for ${displayName}`} type="checkbox" checked={inlineDraft.isDefault} disabled={busy || !!location.isDefault} onChange={event => setInlineDraft(previous => ({ ...previous, isDefault: event.target.checked }))} />
              : location.isDefault ? 'Yes' : 'No'}</td>
            <td style={{ whiteSpace: 'nowrap' }}>{canEdit && <>
              {editingLocationId === location.id ? <>
                <ActionIconButton action="save" label={`Save location ${displayName}`} disabled={busy} onClick={() => saveInlineLocation(location)} />{' '}
                <ActionIconButton action="cancel" label={`Cancel editing ${displayName}`} disabled={busy} onClick={cancelLocationEdit} />
              </> : <>
                <ActionIconButton action="edit" label={`Edit location ${displayName}`} disabled={busy} onClick={() => editLocation(location)} />{' '}
                <ActionIconButton action="delete" label={location.isDefault ? `Delete location ${displayName} (primary location cannot be deleted)` : `Delete location ${displayName}`} disabled={busy || !!location.isDefault} onClick={() => remove(location)} />
              </>}
            </>}</td>
          </tr>
          );
        })}</tbody>
      </table>
      {canEdit && <form onSubmit={save}>
        <h4>Add Location</h4>
        <div className="form-fields" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem' }}>
          {['name', 'airport'].map(field => <label key={field} style={{ ...fieldLabelStyle, textTransform: 'capitalize' }}>{field}<input required disabled={busy} size={field === 'airport' ? 4 : undefined} maxLength={field === 'name' ? 200 : 5} pattern={field === 'airport' ? '[A-Za-z0-9]{3,5}' : undefined} value={draft[field]} onChange={event => setDraft({ ...draft, [field]: event.target.value })} onBlur={field === 'airport' ? () => setDraft(previous => ({ ...previous, airport: normalizeAirportCode(previous.airport) })) : undefined} style={field === 'airport' ? airportCodeInputStyle : inputStyle} /></label>)}
          <div><span>City</span><div style={{ padding: '0.5rem 0' }}>{airportDetails?.city || 'Not provided'}</div></div>
          <div><span>State</span><div style={{ padding: '0.5rem 0' }}>{airportDetails?.state || 'Not provided'}</div></div>
        </div>
        {airportLookup.loading && <p role="status">Validating airport...</p>}
        {airportLookup.error && <p role="alert" style={{ color: '#b91c1c' }}>{airportLookup.error}</p>}
        {airportDetails && <p role="status">{airportDetails.airportCode}{airportDetails.name ? ` - ${airportDetails.name}` : ''}</p>}
        <label style={{ display: 'block', margin: '0.75rem 0' }}><input type="checkbox" checked={!!draft.isDefault} disabled={locations.some(location => location.id === draft.id && location.isDefault)} onChange={event => setDraft({ ...draft, isDefault: event.target.checked })} /> Primary location</label>
        <button disabled={busy} className="form-primary" type="submit"><span aria-hidden="true">💾</span>{busy ? 'Saving...' : 'Save Location'}</button>
      </form>}
    </section>
  );
};

export default OrganizationLocations;